import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { GuestManager } from "@/components/ui/guest-manager";
import { useI18n } from "@/hooks/useI18n";
import { StepProps } from "@/lib/builder/models/builder.model";
import ImageUpload from "../../components/ui/ImageUpload";
import { IGuest } from "../request-trip/models/travel-request.model";
import { uploadItineraryWallpaper } from "@/utils/imageStorage";
import { RichTextEditor } from "@/components/ui/rich-text-editor";

const ItineraryStep = ({
  data,
  onDataChange,
  onImmediateDataChange,
  canProceed,
  setCanProceed,
  isReadOnly = false,
  compact = false,
}: StepProps) => {
  const { t } = useI18n();
  const [localData, setLocalData] = useState(data);

  const handleInputChange = (field: keyof typeof localData, value: string | number) => {
    const newData = localData.update({ [field]: value });
    setLocalData(newData);
    onDataChange(newData);
  };

  const handleImageChange = (newImage: string | null) => {
    const newData = localData.update({ image: newImage });
    setLocalData(newData);
    if (onImmediateDataChange) {
      onImmediateDataChange(newData);
    } else {
      onDataChange(newData);
    }
  };

  // Guest management functions
  const addGuest = () => {
    const newGuest: IGuest = { id: Date.now(), age: 25 };
    const newGuestsData = [...(localData.guests_data || []), newGuest];
    const newData = localData.update({ guests_data: newGuestsData });
    setLocalData(newData);
    onDataChange(newData);
  };

  const removeGuest = () => {
    if ((localData.guests_data || []).length <= 1) return;

    const newGuestsData = (localData.guests_data || []).slice(0, -1);
    const newData = localData.update({ guests_data: newGuestsData });
    setLocalData(newData);
    onDataChange(newData);
  };

  const updateGuestAge = (guestId: number, newAge: number) => {
    const validAge = Math.max(0, newAge);
    const newGuestsData = (localData.guests_data || []).map(guest =>
      guest.id === guestId ? { ...guest, age: validAge } : guest
    );
    const newData = localData.update({ guests_data: newGuestsData });
    setLocalData(newData);
    onDataChange(newData);
  };

  useEffect(() => {
    // Check if we can proceed (basic validation)
    const canProceedNow = localData.title.trim() !== '' &&
      localData.start_date !== '' &&
      localData.end_date !== '';
    setCanProceed(canProceedNow);
  }, [localData, setCanProceed]);


  return (
    <div className="space-y-8 px-16 py-8">

      {/* Image Upload Section */}
      <div className="space-y-4">
        <ImageUpload
          image={localData.image || null}
          onImageChange={handleImageChange}
          maxSizeMB={10}
          disabled={isReadOnly}
          uploadFn={localData.id ? (file) => uploadItineraryWallpaper(localData.id!, file) : undefined}
          heightClass={compact ? 'h-[180px]' : 'h-[350px]'}
        />
      </div>

      {/* Trip Title */}
      <div className="space-y-2">
        <h4 className="text-gray-900">
          {t('itinerary.tripTitle')}
        </h4>

        <Input
          id="title"
          value={localData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          placeholder={t('itinerary.tripTitlePlaceholder')}
          className="text-lg"
          disabled={isReadOnly}
        />
      </div>

      {/* Dates */}
      <div className={`space-y-2`}>
        <h4 className="text-gray-900">
          {t('itinerary.dates')}
        </h4>
        <DateRangePicker
          startDate={localData.start_date}
          endDate={localData.end_date}
          onStartDateChange={(date) => handleInputChange('start_date', date)}
          onEndDateChange={(date) => handleInputChange('end_date', date)}
          startDatePlaceholder={t('common.from')}
          endDatePlaceholder={t('common.to')}
          disabled={isReadOnly}
        />
      </div>

      {/* Guest Management */}
      <div className="space-y-2">
        <h4 className="text-gray-900">
          {t('itinerary.guestsTitle')}
        </h4>
        <GuestManager
          totalGuests={localData.total_guests}
          guests={localData.guests_data || []}
          onAddGuest={addGuest}
          onRemoveGuest={removeGuest}
          onUpdateGuestAge={updateGuestAge}
          disabled={isReadOnly}
        />
      </div>

      {/* Description */}
      <div className="space-y-2">
        <h4 className="text-gray-900">
          {t('itinerary.tripDescription')}
        </h4>
        <RichTextEditor
          value={localData.description}
          onChange={(val) => handleInputChange('description', val)}
          placeholder={t('itinerary.tripDescriptionPlaceholder')}
        />
      </div>
    </div>
  );
};

export default ItineraryStep;